/**
 * Десктопный клиент (T-185): запросы — через плагин HTTP программы (без CORS, cookie входа
 * хранит программа) на сервер, адрес которого задан при первом запуске.
 */

import { fetch as tauriFetch } from '@tauri-apps/plugin-http';

import { http } from '@/api/http';
import { isDesktop, serverUrl } from '@/runtime';

const CONNECT_TIMEOUT_MS = 5_000;
const REQUEST_TIMEOUT_MS = 30_000;

export function pointHttpAt(url: string | null): void {
  http.defaults.baseURL = url ?? '';
}

/**
 * Сервер недоступен — плагин бросает свою ошибку (строку), а axios считает «нет сети» только
 * TypeError с «fetch» в тексте, как у браузера. Без этого офлайн-режим не включится:
 * вместо очереди — «неизвестная ошибка» и выход на страницу входа.
 */
async function desktopFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  // Плагин не обрывает запрос по AbortSignal таймаута axios: без своего таймера запрос к
  // недоступному серверу висит вечно, и офлайн-режим не включается (проверено на запуске).
  const signal = init?.signal;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const gaveUp = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      reject(new TypeError('Failed to fetch: сервер не ответил вовремя'));
    }, REQUEST_TIMEOUT_MS);
    signal?.addEventListener('abort', () => {
      reject(new TypeError('Failed to fetch: запрос отменён'));
    });
  });
  try {
    return await Promise.race([
      tauriFetch(input, { ...init, connectTimeout: CONNECT_TIMEOUT_MS }),
      gaveUp,
    ]);
  } catch (e) {
    throw e instanceof TypeError ? e : new TypeError(`Failed to fetch: ${String(e)}`);
  } finally {
    clearTimeout(timer);
  }
}

export function configureDesktop(): void {
  if (!isDesktop) return;
  http.defaults.adapter = 'fetch';
  http.defaults.env = { ...http.defaults.env, fetch: desktopFetch };
  pointHttpAt(serverUrl());
}
